import { Head } from "@inertiajs/react"
import { useEffect, useMemo, useRef, useState } from "react"

import { AssessorPanel } from "@/components/chat/AssessorView"
import CoachingSurface from "@/components/chat/CoachingSurface"
import type { CoachingPrompt } from "@/components/chat/CoachingSurface"
import DoorsTracker from "@/components/chat/DoorsTracker"
import KaraokePresenter from "@/components/chat/KaraokePresenter"
import SimulationSurface from "@/components/chat/SimulationSurface"
import { useAuth } from "@/hooks/use-auth"
import { useConversation } from "@/hooks/use-conversation"
import { useConversationEvents } from "@/hooks/use-conversation-events"
import { useLiveCoaching } from "@/hooks/useLiveCoaching"
import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem } from "@/types"
import type {
  DoorOpenedPayload,
  SessionDebriefPayload,
} from "@/types/conversation"
import { postJson } from "@/utils/api"

const breadcrumbs: BreadcrumbItem[] = [
  {
    title: "Chat",
    href: "/chat",
  },
]

type FocusedSurface = "coaching" | "simulation"

/**
 * Read a `?conversation_id=<id>` query param, if present and valid. Set by the
 * admin replay listing's "Resume" action to drop straight into an existing
 * conversation rather than starting a fresh one.
 */
function readResumeId(): number | null {
  if (typeof window === "undefined") return null
  const raw = new URLSearchParams(window.location.search).get("conversation_id")
  const id = raw ? Number(raw) : Number.NaN
  return Number.isInteger(id) && id > 0 ? id : null
}

/**
 * Read a `?script=<script_id>` query param. When present, fresh conversations
 * are created in Karaoke mode (the live /chat surfaces driven by a hand-authored
 * script instead of the LLM — see Domains::Counterpoint::Scripts). Goal
 * selection still happens live; the chosen focus starts the scripted opening.
 */
function readScriptId(): string | null {
  if (typeof window === "undefined") return null
  const raw = new URLSearchParams(window.location.search).get("script")
  return raw?.trim() ? raw.trim() : null
}

export default function Chat() {
  const { user } = useAuth()

  // When resuming an existing conversation, skip the coaching intro and land in
  // the simulation surface where the transcript lives.
  const resumeId = useMemo(() => readResumeId(), [])

  // Karaoke mode: fresh conversations created with this config play the
  // authored script through the live surfaces.
  const scriptId = useMemo(() => readScriptId(), [])
  const scriptConfig = useMemo(
    () => (scriptId ? { mode: "scripted", script_id: scriptId } : undefined),
    [scriptId],
  )
  // Once the presenter forks to live, karaoke mode is over: the mic hides and
  // the teleprompter keys disarm so digits type normally.
  const [forked, setForked] = useState(false)
  const karaokeActive = !!scriptId && !forked

  const [focusedSurface, setFocusedSurface] = useState<FocusedSurface>(
    resumeId ? "simulation" : "coaching",
  )
  const [selectedPrompt, setSelectedPrompt] = useState<CoachingPrompt | null>(
    null,
  )
  const [coachingExpanded, setCoachingExpanded] = useState(!resumeId)
  // The latest ambient brief the trainee has revealed; once read, the minimised
  // surface stops advertising "Coach has a note".
  const [readAmbientId, setReadAmbientId] = useState<string | null>(null)
  const {
    conversation,
    startConversation,
    resumeConversation,
    resetConversation,
  } = useConversation()
  const {
    events,
    userMessageIds,
    emitEvent,
    paused,
    resume,
    primeBacklog,
    markHydrated,
    liveCursor,
  } = useConversationEvents(
    conversation?.conversation_id,
    conversation?.ws?.url,
    conversation?.session_token,
  )

  // The live coaching presentation model, folded from the full coach_dialogue_page
  // stream (buffered framing stepper + auto/ambient split). Replaces the old
  // "latest event wins" effect; see hooks/useLiveCoaching.
  const coaching = useLiveCoaching(
    events,
    conversation?.conversation_id,
    emitEvent,
  )
  const ambientUnread =
    !!coaching.ambientNote &&
    !!coaching.ambientNoteId &&
    coaching.ambientNoteId !== readAmbientId

  const handleOptionSelected = async (prompt: CoachingPrompt) => {
    // Guard against duplicate sends
    if (selectedPrompt) return
    // Record selection locally for UI
    setSelectedPrompt(prompt)
    // The chosen focus selects the Score and triggers the GP's opening line.
    // prompt.id is the focus id ("symptoms" | "advocate" | "diagnostic").
    const convo = conversation ?? (await startConversation(scriptConfig))
    if (convo?.conversation_id) {
      try {
        await postJson(`/api/conversations/${convo.conversation_id}/start`, {
          focus: prompt.id,
        })
        // Ensure event polling is active in case it paused on empties
        resume()
      } catch (e) {
        console.error("Failed to start simulation", e)
      }
    }
  }

  const handleContinueToSimulation = () => {
    // Backend will handle stage transition based on coaching_acknowledged event
    // Simple: switch focus; NPC message will arrive on its own time
    setFocusedSurface("simulation")
    setCoachingExpanded(false)
  }

  // Handle "start new conversation" from sidebar toggle
  useEffect(() => {
    const handler = () => {
      void (async () => {
        resetConversation()
        await startConversation(scriptConfig)
        // polling effect will kick in automatically
      })()
    }
    window.addEventListener("npc-new-conversation", handler)
    return () => window.removeEventListener("npc-new-conversation", handler)
  }, [resetConversation, startConversation, scriptConfig])

  // Polling is now managed inside the events hook (fallback-only).
  // We intentionally do not start polling here to avoid duplicate loops.

  // On mount: resume the requested conversation, or auto-start a fresh one.
  useEffect(() => {
    if (conversation) return
    if (resumeId) {
      void resumeConversation(resumeId)
    } else {
      void startConversation(scriptConfig)
    }
  }, [])

  // Hydrate each conversation once, drawing the line between historical backlog
  // and live events. On resume we drain the full history first, then mark the
  // backlog's end as the watermark (the socket delivers only new events from
  // here, which animate). A fresh conversation has no backlog, so everything is
  // live from the start. Keyed per id so switching conversations re-hydrates.
  const hydratedForRef = useRef<number | null>(null)
  useEffect(() => {
    const cid = conversation?.conversation_id
    if (!cid || hydratedForRef.current === cid) return
    hydratedForRef.current = cid
    if (resumeId && cid === resumeId) {
      void primeBacklog().then((w) => markHydrated(w))
    } else {
      markHydrated("")
    }
  }, [resumeId, conversation?.conversation_id, primeBacklog, markHydrated])

  // A fresh conversation re-arms karaoke (mic back, teleprompter live again) and
  // clears the goal-selection lock. Without resetting selectedPrompt, the
  // duplicate-send guard in handleOptionSelected silently blocks re-selecting a
  // focus after "start new conversation" (BUG-2).
  useEffect(() => {
    setForked(false)
    setSelectedPrompt(null)
    setReadAmbientId(null)
  }, [conversation?.conversation_id])

  // A scene-breaking interrupt (surface:"auto") takes the scene: auto-expand the
  // coaching band AND focus it (un-blurring requires the focus, not just the
  // expand — the live surface is a single top band detached from the GP line).
  // The Got-It path (handleContinueToSimulation) restores focus to the sim.
  useEffect(() => {
    if (coaching.phase === "interjection") {
      setCoachingExpanded(true)
      setFocusedSurface("coaching")
    }
  }, [coaching.phase])

  const handleFocusCoaching = async () => {
    const previousSurface = focusedSurface
    setFocusedSurface("coaching")
    setCoachingExpanded(true)
    // Tapping the minimised surface to read an ambient brief marks it read.
    if (ambientUnread && coaching.ambientNoteId) {
      setReadAmbientId(coaching.ambientNoteId)
    }

    // Track surface focus change
    if (emitEvent && previousSurface !== "coaching") {
      await emitEvent("surface_focus_changed", {
        from: previousSurface,
        to: "coaching",
        trigger: "click",
      })
    }
  }

  const handleFocusSimulation = async () => {
    const previousSurface = focusedSurface
    setFocusedSurface("simulation")

    // Track surface focus change
    if (emitEvent && previousSurface !== "simulation") {
      await emitEvent("surface_focus_changed", {
        from: previousSurface,
        to: "simulation",
        trigger: "click",
      })
    }
  }

  const isCoachingFocused = focusedSurface === "coaching"
  const isSimulationFocused = focusedSurface === "simulation"

  // The teleprompter "advance" key may only fire once the scripted opening has
  // landed (otherwise the message endpoint 409s until the session is ready).
  const karaokeReady = useMemo(
    () => (events ?? []).some((e) => e.type === "npc_response_complete"),
    [events],
  )

  // Real-flow safety net (BUG-1): the scripted opening emits a framing run after
  // goal selection, which collapses the surface via the Got-It path. The real
  // LLM flow emits only a cold open (no framing), so the surface would otherwise
  // stay expanded at goal_selection forever. Once a focus is chosen and the NPC
  // has spoken (karaokeReady) with no framing having followed, hand off to the
  // simulation. Scoped to the real flow (!scriptId): the scripted demo always
  // has framing and never needs this — and gating it out keeps a delivery-order
  // race (cold open folded a tick before its framing pages) from ever collapsing
  // the demo's multi-page framing run.
  useEffect(() => {
    if (
      !scriptId &&
      selectedPrompt &&
      coaching.phase === "goal_selection" &&
      karaokeReady
    ) {
      void handleContinueToSimulation()
    }
  }, [scriptId, selectedPrompt, coaching.phase, karaokeReady])

  // A focus has been chosen and we're waiting for the opening to arrive (the
  // real flow's Open is 2–3 LLM calls — several seconds). Show a "preparing"
  // state instead of the now-stale, locked goal-selection prompts so the surface
  // never reads as frozen. In the scripted flow framing arrives near-instantly,
  // so this is a momentary flicker at most.
  const selectionPending =
    !!selectedPrompt && coaching.phase === "goal_selection" && !karaokeReady

  // The finale: the latest session_debrief payload (the closing turn emits one).
  // Rendered as the "What we saw" card at the foot of the simulation transcript.
  const debrief = useMemo(() => {
    const found = (events ?? []).filter((e) => e.type === "session_debrief")
    return found[found.length - 1]?.payload as SessionDebriefPayload | undefined
  }, [events])

  // Doors surfaced so far ("open doors to getting help"), folded from the
  // transport stream — drives the persistent DoorsTracker.
  const openedDoors = useMemo(
    () =>
      (events ?? [])
        .filter((e) => e.type === "door_opened")
        .map((e) => e.payload as DoorOpenedPayload),
    [events],
  )

  // The pre-conversation framing run takes the whole screen (a frosted overlay
  // over the blurred conversation). Gated on `framing` so a mid-conversation
  // interrupt (phase "interjection") never goes fullscreen, and on `!scriptId`
  // so the byte-aligned scripted demo keeps its current inline framing untouched
  // (the takeover is a real-flow enhancement). While it's up the rest of the page
  // is inert so focus/AT can't reach the conversation behind it.
  const takeoverActive =
    !scriptId && coaching.phase === "framing" && coachingExpanded

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Chat" />

      <div className="flex h-full flex-1 flex-col overflow-hidden">
        {/* Coaching Surface - Top. Event-stream driven via useLiveCoaching:
            goal-selection prompts, a buffered framing stepper, an auto-expanding
            interrupt, and a tap-to-reveal ambient brief. */}
        <CoachingSurface
          userName={user?.name ?? "Guest"}
          onOptionSelected={(prompt) => void handleOptionSelected(prompt)}
          onCoachingComplete={handleContinueToSimulation}
          // Only offer the "back to conversation" escape once a conversation
          // exists to return to (so it never flashes during the cold-open
          // greeting). Prevents the idle "Tap for help" lockup (BUG-5).
          onDismiss={karaokeReady ? handleContinueToSimulation : undefined}
          isExpanded={coachingExpanded}
          isTakeover={takeoverActive}
          isFocused={isCoachingFocused}
          onFocus={() => void handleFocusCoaching()}
          emitEvent={emitEvent}
          mode={coaching.coachMode}
          prompts={
            !selectedPrompt && coaching.phase === "goal_selection"
              ? coaching.prompts
              : undefined
          }
          question={
            selectionPending
              ? "Setting up your conversation…"
              : coaching.question
          }
          showTypingIndicator={
            selectionPending ||
            (coaching.phase !== "goal_selection" &&
              coaching.content === undefined &&
              coaching.ambientNote === undefined)
          }
          content={coaching.content}
          currentStep={coaching.cursor}
          totalSteps={coaching.totalSteps}
          hasNextPage={coaching.hasNextPage}
          onNext={coaching.onNext}
          ambientNote={coaching.ambientNote}
          ambientUnread={ambientUnread}
        >
          {/* During the intro the doors reveal gradually inside the takeover
              card — locked and nameless — paced to the coaching cards. They
              appear from card 2, where the metaphor is introduced. */}
          {takeoverActive && coaching.cursor > 0 && (
            <DoorsTracker
              opened={openedDoors}
              revealCount={coaching.cursor}
              tone="bare"
              className="w-full max-w-md"
            />
          )}
        </CoachingSurface>

        {/* Everything below the coaching surface goes inert while the takeover
            is up (display:contents keeps the flex layout identical). */}
        <div
          className="contents"
          inert={takeoverActive}
          aria-hidden={takeoverActive ? true : undefined}
        >
          {/* Doors opened — a persistent counter that appears once the
            conversation is underway and ticks up as GP-held disclosures
            surface (the live counterpart to the debrief's door tally). */}
          {(karaokeReady || openedDoors.length > 0) && (
            <DoorsTracker
              opened={openedDoors}
              className="mx-auto mt-3 w-full max-w-3xl"
            />
          )}

          {/* Simulation Surface - Bottom */}
          <SimulationSurface
            conversationId={conversation?.conversation_id}
            events={events}
            liveCursor={liveCursor}
            isFocused={isSimulationFocused}
            isBlurred={isCoachingFocused}
            onFocus={() => void handleFocusSimulation()}
            emitEvent={emitEvent}
            userMessageIds={userMessageIds}
            karaokeMode={karaokeActive}
            debrief={debrief}
          />
          {/* Karaoke mode: teleprompter keys (1 = advance, 0 = fork to live) */}
          <KaraokePresenter
            conversationId={conversation?.conversation_id}
            scriptId={scriptId}
            enabled={karaokeActive}
            ready={karaokeReady}
            onForked={() => setForked(true)}
          />
          {/* Admin-only assessor flash (toggle-gated via the chat sidebar's
            "Assessor View"; self-hides when off). A fixed overlay so it never
            reflows the trainee transcript. Reads assessor_observation events
            directly — the firewall keeps grades out of the chat thread. */}
          <div className="pointer-events-none fixed right-6 bottom-28 z-40 w-72">
            <AssessorPanel
              events={events ?? []}
              currentEventIndex={(events?.length ?? 1) - 1}
            />
          </div>
          {/* Conversation meta */}
          {conversation && (
            <div className="flex items-center gap-3 px-6 py-2 text-xs text-neutral-500">
              <span>Conversation #{conversation.conversation_id}</span>
              {events && events.length > 0 && (
                <span>
                  Events: {events.length} (last:{" "}
                  {events[events.length - 1]?.type})
                </span>
              )}
              {paused && (
                <button className="rounded border px-2 py-0.5" onClick={resume}>
                  Resume events
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  )
}
